import type {
    IProductImage,
    ProductListItemModel,
    ProductListPriceInfo,
    ProductListVariantsModel,
    ProductName,
    Splash,
    SplashModel
} from 'autogen/swagger/all';
import Card from 'components/generic/card';
import type { ICard } from 'components/generic/card/shared/types';
import ProductActionRenderer from 'components/generic/productCTA/ProductActionRenderer';
import { compactActionComponents } from 'components/generic/productCTA/shared/productActionComponents';
import type { IProductActionButton } from 'components/generic/productCTA/shared/types';
import ProductFavoriteButton from 'components/generic/productFavoriteButton/ProductFavoriteButton';
import ProductImage from 'components/generic/productImage';
import { ProductNameCompact } from 'components/generic/productName/ProductName';
import LinkAction from 'components/shared/Routing/components/LinkAction/LinkAction';
import type { LinkAction as LinkActionType } from 'components/shared/Routing/components/LinkAction/shared/types';
import { TagSplash } from 'components/shared/Splash';
import SponsoredSplash from 'components/shared/Splash/components/SponsoredSplash';
import imageOverlayStyle from 'components/shared/style/imageOverlay';
import { type EventParamDecorated, GA4Tracking } from 'modules/tracking/GA4';
import React, { type ComponentPropsWithoutRef, type ReactNode, useMemo } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import serverSideTracking from '../../shared/helpers';
import { TrackingEvent } from '../../shared/types';
import useThemeHandler from '../../themes/useThemeHandler';
import VariantRenderer from '../Variant/VariantRenderer';
import HorizontalSplashOverlay from './components/SplashOverlay';
import modelMapper from './shared/helpers';
import type { ThemeType } from './shared/types';
import PriceModule from 'components/generic/productPrice';

/* #region styles */
const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing4};
`;

const StyledCardContent = styled(Card.Content)`
    display: grid;
    grid-template-columns: 115px 1fr;
    padding: 0;

    @media (max-width: 350px) {
        grid-template-columns: 100px 1fr;
    }

    a,
    button,
    label {
        z-index: 3;
    }
`;

const AnchorOverlay = styled(LinkAction)`
    position: absolute;
    width: 100%;
    height: 100%;

    && {
        z-index: 2;
    }

    &:focus {
        outline: none;
    }
`;

const ProductImageContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => `${theme.Spacing16} ${theme.Spacing8}`};
    background-color: ${({ theme }) => theme.ColorBackgroundPrimary};

    &:after {
        content: '';
        ${imageOverlayStyle}
    }
`;

const StyledProductFavoriteButton = styled(ProductFavoriteButton)`
    position: absolute;
    top: 0;
    left: 0;
`;

const ProductDescriptionContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr minmax(40px, auto);
    grid-template-areas:
            "text text"
            "price button";
    gap: ${({ theme }) => theme.Spacing4};
    padding: ${({ theme }) => theme.Spacing16};

    ${({ theme }) => theme.ProductDescriptionContainer}
`;

const StyledCard = styled(Card)`
    position: relative;
    height: 100%;
    background-color: ${({ theme }) => theme.ColorBackgroundPrimary};
    border: 1px solid ${({ theme }) => theme.ColorBorderTertiary} !important;
    border-radius: ${({ theme }) => theme.RadiusMedium};
    ${({ theme }) => theme.StyledCard}

    &:has(${AnchorOverlay}:focus-visible) {
        outline: ${({ theme }) => theme.Spacing4} auto ${({ theme }) => theme.ColorBorderFocusRing};
        outline-offset: ${({ theme }) => theme.Spacing4};
        border-radius: ${({ theme }) => theme.RadiusMedium};
    }
`;

const StyledTagSplash = styled(TagSplash)`
    ${({ theme }) => theme.TagSplash}
`;

const TextContainer = styled.div`
    grid-area: text;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing4};
    min-width: 0;
`;

const StyledProductNameCompact = styled(ProductNameCompact)`
    margin-top: ${({ theme }) => theme.Spacing8};
    margin-bottom: ${({ theme }) => theme.Spacing4};
`;

const StyledProductPrice = styled(PriceModule)`
    grid-area: price;
    align-self: end;
`;

const StyledProductActionRenderer = styled(ProductActionRenderer)`
    grid-area: button;
    align-self: end;
`;

const StyledSponsoredSplash = styled(SponsoredSplash)`
    margin-top: -20px; // SponsoredSplash height + 4px 
`;

const SplashContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.Spacing4};
`;

/* #endregion */

const HorizontalProductItem = React.forwardRef<
    HTMLDivElement,
    HorizontalProductItemProps & {
        model: ProductListItemModel;
        recommendationPlacement?: string;
        themeType?: ThemeType;
        pageContext?: 'ProductList';
    }
>(({ model, recommendationPlacement, themeType, pageContext, ...restProps }, ref) => {
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    const { productModel, actionModel } = useMemo(() => modelMapper(model, recommendationPlacement), []);
    const {
        listName,
        image,
        price,
        linkAction,
        trackModel,
        splashes,
        dataLayerDto,
        id,
        favoriteStatus,
        variants,
        isSponsored
    } = productModel;

    listName.lines = listName.lines.filter((line) => line.type !== 'Variant');
    const standardName = listName?.lines.find((name) => name.type === 'Name');
    const theme = useThemeHandler(themeType);

    const UAEventLinkTracking = () => {
        const eventParams: EventParamDecorated[] = [{ param_name: 'link_type', param_value: 'internal' }];

        if (actionModel) {
            eventParams.push({
                param_name: 'recommendation_type',
                param_value: actionModel.trackingContext.eventCategory
            });
        }

        GA4Tracking({
            eventName: 'link_click',
            context: 'recommendation',
            category: 'navigation',
            eventParams
        });
    };

    return (
        <ThemeProvider theme={theme}>
            <Wrapper>
                {pageContext === 'ProductList' && isSponsored && <StyledSponsoredSplash productId={id} />}
                <BaseHorizontalProductItem
                    ref={ref}
                    data-datalayer={JSON.stringify(dataLayerDto)}
                    onClick={
                        trackModel
                            ? () => serverSideTracking(JSON.stringify(trackModel), TrackingEvent.ProductClick)
                            : undefined
                    }
                    image={image}
                    productLinkAction={linkAction}
                    name={listName}
                    splashes={splashes}
                    price={price}
                    variants={variants}
                    actionModel={{ model: actionModel }}
                    favoriteModel={{ isFavorite: favoriteStatus.favoriteStatus === 'IsFavorite', id }}
                    overlayRenderer={(overlayModel) =>
                        overlayModel?.productLinkAction && (
                            <AnchorOverlay
                                action={overlayModel.productLinkAction}
                                aria-label={standardName?.value}
                                onClick={UAEventLinkTracking}
                            />
                        )
                    }
                    {...restProps}
                />
            </Wrapper>
        </ThemeProvider>
    );
});

type FavoriteActionModel = {
    id: number;
    isFavorite: boolean;
};

type HorizontalProductItemRenderers = {
    imageRenderer?: ((imageModel?: IProductImage) => ReactNode) | null;
    overlayRenderer?: ((overlayModel?: { productLinkAction?: LinkActionType }) => ReactNode) | null;
    splashRenderer?: ((splashModel?: SplashModel) => ReactNode) | null;
    tagRenderer?: ((tagModel?: SplashModel) => ReactNode) | null;
    productNameRenderer?: ((productNameModel?: ProductName) => ReactNode) | null;
    priceRenderer?: ((priceModel?: ProductListPriceInfo) => ReactNode) | null;
    variantRenderer?: ((variantModel?: ProductListVariantsModel) => ReactNode) | null;
    favoriteActionRenderer?: ((favoriteActionModel?: FavoriteActionModel) => ReactNode) | null;
    productActionRenderer?: ((productActionModel?: IProductActionButton) => ReactNode) | null;
};

type HorizontalProductItemProps = {
    image?: IProductImage;
    productLinkAction?: LinkActionType;
    name?: ProductName;
    splashes?: SplashModel;
    price?: ProductListPriceInfo;
    actionModel?: IProductActionButton;
    variants?: ProductListVariantsModel;
    favoriteModel?: FavoriteActionModel;
} & ICard &
    HorizontalProductItemRenderers &
    ComponentPropsWithoutRef<'div'>;

export const BaseHorizontalProductItem = React.forwardRef<HTMLDivElement, HorizontalProductItemProps>((props, ref) => {
    const {
        image,
        productLinkAction,
        name,
        splashes,
        price,
        variants,
        favoriteModel,
        actionModel,
        size = 'sm',

        // Renderers
        imageRenderer = (imageModel) =>
            imageModel && (
                <ProductImage
                    width={90}
                    maxWidth="90px"
                    image={imageModel}
                    transformType="WidthScale"
                    aspectRatio="3/4"
                />
            ),

        overlayRenderer = (overlayModel) =>
            overlayModel?.productLinkAction && <AnchorOverlay action={overlayModel.productLinkAction} />,

        splashRenderer = (splashModel) => splashModel && <HorizontalSplashOverlay {...splashModel} />,

        tagRenderer = (tagModel) => (
            <>
                {tagModel?.primary && <StyledTagSplash {...tagModel.primary} size="md" />}
                {tagModel?.secondary && <StyledTagSplash {...tagModel.secondary} size="md" />}
            </>
        ),

        productNameRenderer = (productNameModel) =>
            productNameModel && (
                <StyledProductNameCompact
                    productName={{ lines: productNameModel.lines.filter((line) => line.type !== 'Variant') }}
                />
            ),

        variantRenderer = (variantModel) => variantModel && <VariantRenderer variants={variantModel} size="sm" />,

        priceRenderer = (priceModel) => priceModel && <StyledProductPrice {...priceModel} size="sm" />,

        favoriteActionRenderer = (favoriteActionModel) =>
            favoriteActionModel && (
                <StyledProductFavoriteButton
                    initialFavoriteState={favoriteActionModel.isFavorite}
                    productId={favoriteActionModel.id}
                    variant="favoriteGhostHorizontal"
                    iconSize="lg"
                />
            ),

        productActionRenderer = (productActionModel) =>
            productActionModel && (
                <StyledProductActionRenderer
                    model={productActionModel.model}
                    overriddenActionComponents={compactActionComponents}
                />
            ),
        ...restProps
    } = props;

    return (
        <StyledCard ref={ref} size={size} backgroundColor="transparent" {...restProps}>
            {overlayRenderer?.({ productLinkAction })}

            <StyledCardContent>
                <ProductImageContainer>
                    {imageRenderer?.(image)}

                    {splashRenderer?.(splashes)}

                    {favoriteActionRenderer?.(favoriteModel)}
                </ProductImageContainer>

                <ProductDescriptionContainer>
                    <TextContainer>
                        {(splashes?.primary || splashes?.secondary) && (
                            <SplashContainer>{tagRenderer?.(splashes)}</SplashContainer>
                        )}

                        {productNameRenderer?.(name)}

                        {variantRenderer?.(variants)}
                    </TextContainer>

                    {priceRenderer?.(price)}

                    {productActionRenderer?.(actionModel)}
                </ProductDescriptionContainer>
            </StyledCardContent>
        </StyledCard>
    );
});

export default HorizontalProductItem;
